import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { VStack } from "@whoosh/design-system/src/components/primitive";

import { PatronBookingAffectedRateItemLine } from "./PatronBookingAffectedRateItemLine";
import { PatronBookingRateChangeConfirmModalRateInitializationData } from "./PatronBookingRateChangeConfirmModal";
import { PatronBookingRateChangeConfirmModalContentQuery } from "./__generated__/PatronBookingRateChangeConfirmModalContentQuery.graphql";

export const PatronBookingRateChangeConfirmModalContent = ({
  bookingId,
  rateUpdates,
}: {
  bookingId?: string;
  rateUpdates: PatronBookingRateChangeConfirmModalRateInitializationData["rateUpdates"];
}) => {
  const [queryRef, loadQuery] =
    useQueryLoader<PatronBookingRateChangeConfirmModalContentQuery>(
      PatronBookingRateChangeConfirmModalContentQueryString
    );

  useEffect(() => {
    if (bookingId) {
      loadQuery({ bookingId });
    }
  }, [loadQuery, bookingId]);

  return (
    <Suspense fallback={null}>
      {queryRef ? (
        <PatronBookingRateChangeConfirmModalContentQueryRenderer
          queryRef={queryRef}
          rateUpdates={rateUpdates}
        />
      ) : null}
    </Suspense>
  );
};

export const PatronBookingRateChangeConfirmModalContentQueryRenderer = ({
  queryRef,
  rateUpdates,
}: {
  queryRef: PreloadedQuery<PatronBookingRateChangeConfirmModalContentQuery>;
  rateUpdates: PatronBookingRateChangeConfirmModalRateInitializationData["rateUpdates"];
}) => {
  const data = usePreloadedQuery(
    PatronBookingRateChangeConfirmModalContentQueryString,
    queryRef
  );

  const sourceBookingRate = data.booking?.rate;
  const durationMins = data.booking?.durationMins ?? 0;
  const playerCount = data.booking?.rounds?.count ?? 0;

  return (
    <VStack>
      <VStack gap={4} paddingTop={6}>
        {rateUpdates?.map((dr) => {
          const holeCount =
            data.booking?.rounds?.edges?.find(
              (r) => r?.node?.id === dr.targetId
            )?.node?.courseLayout?.holeCount ?? "NONE";
          return (
            <PatronBookingAffectedRateItemLine
              key={dr.targetId}
              rateId={dr.rateId}
              targetId={dr.targetId}
              holeCount={holeCount}
              durationMins={durationMins}
              playerCount={playerCount}
              slotTargetId={dr?.slotTargetId ?? ""}
              sourceBookingRateId={sourceBookingRate?.id ?? ""}
            />
          );
        })}
      </VStack>
    </VStack>
  );
};
export const PatronBookingRateChangeConfirmModalContentQueryString = graphql`
  query PatronBookingRateChangeConfirmModalContentQuery($bookingId: ID!) {
    booking: node(id: $bookingId) {
      ... on StandardBooking {
        id
        durationMins
        rounds(nonPlayingHost: false) {
          count
          edges {
            node {
              id
              courseLayout {
                holeCount
              }
            }
          }
        }
        rate {
          id
        }
      }
    }
  }
`;
